import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token and instanceId to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  
  // Add instanceId for multi-tenant support
  const user = localStorage.getItem('user');
  const selectedInstanceId = localStorage.getItem('selectedInstanceId');
  
  if (selectedInstanceId) {
    // Admin-master selected instance
    config.headers['X-Instance-ID'] = selectedInstanceId;
  } else if (user) {
    try {
      const userData = JSON.parse(user);
      if (userData.instance_id) {
        config.headers['X-Instance-ID'] = userData.instance_id;
      }
    } catch (e) {
      // Invalid user data
    }
  }
  
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  register: (data) => api.post('/auth/register', data),
  getMe: () => api.get('/auth/me'),
};

// Users
export const usersAPI = {
  getAll: () => api.get('/users'),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
};

// Profiles
export const profilesAPI = {
  getAll: () => api.get('/profiles'),
  create: (data) => api.post('/profiles', data),
  update: (id, data) => api.put(`/profiles/${id}`, data),
  delete: (id) => api.delete(`/profiles/${id}`),
};

// Categories
export const categoriesAPI = {
  getAll: () => api.get('/categories'),
  create: (data) => api.post('/categories', data),
  update: (id, data) => api.put(`/categories/${id}`, data),
  delete: (id) => api.delete(`/categories/${id}`),
};

// Products
export const productsAPI = {
  getAll: (params) => api.get('/products', { params }),
  getById: (id) => api.get(`/products/${id}`),
  create: (data) => api.post('/products', data),
  update: (id, data) => api.put(`/products/${id}`, data),
  delete: (id) => api.delete(`/products/${id}`),
};

// Combos
export const combosAPI = {
  getAll: () => api.get('/combos'),
  create: (data) => api.post('/combos', data),
  update: (id, data) => api.put(`/combos/${id}`, data),
  delete: (id) => api.delete(`/combos/${id}`),
};

// Payment Methods
export const paymentMethodsAPI = {
  getAll: () => api.get('/payment-methods'),
  create: (data) => api.post('/payment-methods', data),
  update: (id, data) => api.put(`/payment-methods/${id}`, data),
  delete: (id) => api.delete(`/payment-methods/${id}`),
};

// Customers
export const customersAPI = {
  getAll: (params) => api.get('/customers', { params }),
  create: (data) => api.post('/customers', data),
  update: (id, data) => api.put(`/customers/${id}`, data),
  delete: (id) => api.delete(`/customers/${id}`),
};

// Tables
export const tablesAPI = {
  getAll: () => api.get('/tables'),
  create: (data) => api.post('/tables', data),
  update: (id, data) => api.put(`/tables/${id}`, data),
  delete: (id) => api.delete(`/tables/${id}`),
};

// Cashiers
export const cashiersAPI = {
  getAll: () => api.get('/cashiers'),
  getById: (id) => api.get(`/cashiers/${id}`),
  create: (data) => api.post('/cashiers', data),
  update: (id, data) => api.put(`/cashiers/${id}`, data),
  open: (id, data) => api.post(`/cashiers/${id}/open`, data),
  close: (id, data) => api.post(`/cashiers/${id}/close`, data),
  delete: (id) => api.delete(`/cashiers/${id}`),
};

// Orders
export const ordersAPI = {
  getAll: (params) => api.get('/orders', { params }),
  getById: (id) => api.get(`/orders/${id}`),
  create: (data) => api.post('/orders', data),
  update: (id, data) => api.put(`/orders/${id}`, data),
  delete: (id) => api.delete(`/orders/${id}`),
};

// Sales
export const salesAPI = {
  getAll: (params) => api.get('/sales', { params }),
  create: (data) => api.post('/sales', data),
};

// Dashboard
export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
  getRecentSales: () => api.get('/dashboard/recent-sales'),
};

// Reports
export const reportsAPI = {
  getSales: (params) => api.get('/reports/sales', { params }),
  getSalesPDF: (params) => api.get('/reports/sales/pdf', { params, responseType: 'blob' }),
  getCashier: (params) => api.get('/reports/cashiers', { params }),
  getCashierPDF: (params) => api.get('/reports/cashiers/pdf', { params, responseType: 'blob' }),
  getStock: () => api.get('/reports/stock'),
  getStockPDF: () => api.get('/reports/stock/pdf', { responseType: 'blob' }),
};

// Support / Tickets
export const supportAPI = {
  createTicket: (data) => api.post('/support/tickets', data),
  getTickets: (params) => api.get('/support/tickets', { params }),
  getTicket: (id) => api.get(`/support/tickets/${id}`),
  updateTicket: (id, data) => api.put(`/support/tickets/${id}`, data),
};

// Print
export const printAPI = {
  getReceipt: (saleId) => api.get(`/print/receipt/${saleId}`),
  getOrder: (orderId) => api.get(`/print/order/${orderId}`),
};

// Seed
export const seedAPI = {
  seed: () => api.post('/seed'),
};

export default api;
